import { Stack, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { useHistory, useParams } from "react-router";
import { useActionRequest } from "../../../../../../common/hooks/api";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { Api } from "../../../../../../common/service/Api";
import { CollectionContext } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UUIDString } from "../../../../../../common/service/server/Types";
import { PageHeader } from "../../../../../components/layout/header/Header";
import { StandardPageWrapper } from "../../../../../components/layout/StandardPageWrapper";
import { DataSourceReceiptCards } from "../../../../../components/organisms/DataSourceReceiptCards";
import { usePrimaryOrganizationId } from "../../../../../hooks";
import { useCollectionGroup } from "../../../../../hooks/useCollectionGroups";
import { useReceivedForCollectionGroups } from "../../../../../hooks/useReceivedForCollectionGroups";
import { RouteHelpers } from "../../../../../root/routes";
import { ProgressFooter, ProgressFooterActions } from "../../../../get-compliant/ProgressFooter";

type Params = {
  dataSubjectTypeId: UUIDString;
};

export const DataReceiptForGroupPage = () => {
  const orgId = usePrimaryOrganizationId();
  const { dataSubjectTypeId: collectionGroupId } = useParams<Params>();

  const history = useHistory();

  const [collectionGroup, collectionGroupsRequest] = useCollectionGroup(orgId, collectionGroupId);
  const context: CollectionContext[] =
    collectionGroup?.collectionGroupType == "EMPLOYMENT" ? ["EMPLOYEE"] : ["CONSUMER"];
  const receiptData = useReceivedForCollectionGroups(
    orgId,
    [collectionGroup].filter((x) => x),
    context,
  );
  const { data: dataMap } = useDataMap(orgId);
  const groups = useMemo(() => [collectionGroup], [collectionGroup]);

  const isEmployment = collectionGroup?.collectionGroupType === "EMPLOYMENT";

  const { fetch: completeTasks } = useActionRequest({
    api: () => Api.tasks.completeTasks(orgId),
  });

  const handleDone = () => completeTasks();

  const isFinished = receiptData.complete;

  return (
    <StandardPageWrapper
      requests={[
        collectionGroupsRequest,
        receiptData.requests.dataMapRequest,
        receiptData.requests.sourcesRequest,
      ]}
      footer={
        <ProgressFooter
          actions={
            <ProgressFooterActions
              nextContent={"Done"}
              nextDisabled={!isFinished}
              prevOnClick={() =>
                history.push(
                  RouteHelpers.dataMap.collectionGroup.workflow.sourceAssociation(
                    collectionGroupId,
                  ),
                )
              }
              nextOnClick={handleDone}
            />
          }
        />
      }
    >
      <PageHeader titleContent={collectionGroup?.name} />
      <Typography>
        Your third party collection sources are listed below. Identify which categories of personal
        information you collect from each source.
      </Typography>

      <Stack spacing={3} className="my-lg">
        <DataSourceReceiptCards
          dataMap={dataMap}
          receivedData={receiptData}
          groups={groups}
          consumerDirectlyName={isEmployment ? "Worker" : "Consumer"}
        />
      </Stack>
    </StandardPageWrapper>
  );
};
